import { Injectable } from '@angular/core';
import { Appointment } from '../models/appointment.model';
import { AppointmentItem } from '../models/appointment-item.model';
import { HttpClient, HttpEventType, HttpHeaders } from '@angular/common/http';
import { filter, map, Observable, switchMap } from 'rxjs';
import { CustomHttpHeaderName } from '../types/http.types';
import { ApplyCodePayload, UpdateAppointmentItemsPayload, UpdateAppointmentPayload } from './appointment.service.types';
import {
  AppointmentCalendarEvent,
  AppointmentTokenResolution,
  ExpertRating,
  SignedCustomFieldFileUpload,
} from './appointment-api.types';
import { FileUploadEvent } from '../types/file-upload.types';
import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class AppointmentApiService {
  private dataUrl: string = 'appointments/';

  constructor(private http: HttpClient) {}

  /** `ignoreLoading` skips the global loading indicator - for background status polling. */
  getOne(appointmentId: number, options?: { ignoreLoading?: boolean }) {
    const headers = options?.ignoreLoading
      ? new HttpHeaders({ [CustomHttpHeaderName.IGNORE_LOADING]: 'true' })
      : undefined;
    return this.http.get<Appointment>(this.dataUrl + appointmentId, { headers });
  }

  getIcs(appointmentId: number) {
    return this.http.get(`${this.dataUrl}${appointmentId}/ics`, { responseType: 'text' });
  }

  getIcsUrl(appointmentId: number) {
    return `${environment.apiUrl}${this.dataUrl}${appointmentId}/ics`;
  }

  uploadCustomFieldFile(appointmentId: number, customFieldId: number, file: File): Observable<FileUploadEvent> {
    // the item shows its own progress, so the global loading indicator stays off
    const signedUpload$ = this.http.post<SignedCustomFieldFileUpload>(
      `${this.dataUrl}${appointmentId}/custom-field-files`,
      { customFieldId, fileName: file.name, mimeType: file.type, size: file.size },
      { headers: new HttpHeaders({ [CustomHttpHeaderName.IGNORE_LOADING]: 'true' }) },
    );

    return signedUpload$.pipe(
      switchMap(({ uuid, url, fields }) => {
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
        formData.append('file', file, file.name);

        return this.http
          .post(url, formData, {
            headers: new HttpHeaders({ [CustomHttpHeaderName.SKIP_INTERCEPTOR]: 'true' }),
            reportUploadProgress: true,
            observe: 'events',
          })
          .pipe(
            filter((event) => event.type === HttpEventType.UploadProgress || event.type === HttpEventType.Response),
            map((event): FileUploadEvent =>
              event.type === HttpEventType.UploadProgress
                ? { progress: Math.round((100 * event.loaded) / (event.total ?? file.size)) }
                : { uuid },
            ),
          );
      }),
    );
  }

  getCalendarEvent(appointmentId: number) {
    return this.http.get<AppointmentCalendarEvent>(`${this.dataUrl}${appointmentId}/calendar-event`);
  }

  getOneByToken(token: string) {
    return this.http.get<AppointmentTokenResolution>(`${this.dataUrl}token/${token}`);
  }

  /** The completed appointment's receipt, rendered by the API (the same template the salon prints). */
  getReceiptHtml(token: string, lang: string) {
    return this.http.get<{ html: string }>(`${this.dataUrl}token/${token}/receipt`, { params: { lang } });
  }

  getExpertRatings(token: string) {
    return this.http.get<{ expertRatings: ExpertRating[]; googleReviewUrl: string | null }>(
      `${this.dataUrl}token/${token}/expert-ratings`,
    );
  }

  createExpertRating(token: string, payload: { expertId: number; rating: number }) {
    return this.http.post<{ expertRating: ExpertRating }>(`${this.dataUrl}token/${token}/expert-ratings`, payload);
  }

  createOne(payload: Partial<Appointment>) {
    return this.http.post<Appointment>(this.dataUrl, payload);
  }

  updateOne(appointmentId: number, payload: UpdateAppointmentPayload) {
    return this.http.patch<Appointment>(this.dataUrl + appointmentId, payload);
  }

  resetFailedPayment(appointmentId: number, options?: { ignoreLoading?: boolean }) {
    const headers = options?.ignoreLoading
      ? new HttpHeaders({ [CustomHttpHeaderName.IGNORE_LOADING]: 'true' })
      : undefined;
    return this.http.post<Appointment>(this.dataUrl + appointmentId + '/reset-failed-payment', {}, { headers });
  }

  // TODO: refactor this into an action part of a checkout process (needs overall checkout refactoring)
  // and add other validations too (i.e. missing start times or client data due to skipped step)
  validateAvailability(appointmentId: number) {
    return this.http.post(`${this.dataUrl}${appointmentId}/validate-availability`, {});
  }

  addItem(item: AppointmentItem, addonsServiceItemIds: number[] = []) {
    const { appointmentId, choiceId, expertId, serviceItemId, startTime } = item;
    return this.http.post<Appointment>(this.dataUrl + 'item/', {
      appointmentId,
      choiceId,
      expertId,
      serviceItemId,
      startTime,
      addonsServiceItemIds,
    });
  }

  updateItem(id: number, item: Partial<AppointmentItem>, addonsServiceItemIds: number[] = undefined) {
    const { choiceId, expertId, startTime } = item;
    return this.http.patch<Appointment>(this.dataUrl + 'item/' + id, {
      choiceId,
      expertId,
      startTime,
      addonsServiceItemIds,
    });
  }

  deleteItem(orderItem: AppointmentItem) {
    return this.http.delete<Appointment>(this.dataUrl + 'item/' + orderItem.id);
  }

  updateItems(appointmentId: number, payload: UpdateAppointmentItemsPayload) {
    return this.http.patch<Appointment>(`${this.dataUrl}${appointmentId}/items`, payload);
  }

  applyCode(appointmentId: number, payload: ApplyCodePayload) {
    return this.http.post<Appointment>(`${this.dataUrl}${appointmentId}/code`, payload);
  }

  unapplyGiftCard(appointmentId: number, giftCardId: number) {
    return this.http.delete<Appointment>(`${this.dataUrl}${appointmentId}/gift-card/${giftCardId}`);
  }

  unapplyPromoCode(appointmentId: number, promoApplicationId: number) {
    return this.http.delete<Appointment>(`${this.dataUrl}${appointmentId}/promo-code/${promoApplicationId}`);
  }
}
